import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  TextField,
} from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import toast, { Toaster } from "react-hot-toast";
import * as services from "./../../services/saleng";
import AppBreadcrumbs from "./../../components/AppBreadcrumbs";
import CategoryFormDialog from "./CategoryFormDialog";
import SubCategoryFormDialog from "./SubCategoryFormDialog";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SearchIcon from "@mui/icons-material/Search";

const Products = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryInitialData, setCategoryInitialData] = useState(null);

  const [subCategoryDialogOpen, setSubCategoryDialogOpen] = useState(false);
  const [subCategoryInitialData, setSubCategoryInitialData] = useState(null);

  const [search, setSearch] = useState("");

  const filtered = rows.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleEditClick = (id) => () => {
    const editRow = categories.find((row) => row.id === id);
    setCategoryInitialData(null);
    setCategoryDialogOpen(true);
    setTimeout(() => {
      setCategoryInitialData(editRow);
    }, 0);
  };

  const handleEditSubCategoryClick = (id) => () => {
    const editRow = subCategories.find((row) => row.id === id);
    setSubCategoryInitialData(null);
    setSubCategoryDialogOpen(true);
    setTimeout(() => {
      setSubCategoryInitialData(editRow);
    }, 0);
  };

  const handleOpen = (category) => {
    setSelectedCategory(category.name);
    setSubCategories(category.subCategories || []);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const columns = [
    { field: "id", headerName: "ลำดับ", width: 70 },
    {
      field: "image",
      headerName: "รูปภาพ",
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <img
            src={params.value}
            style={{ width: "50px", height: "50px", objectFit: "cover" }}
            alt=""
          />
        ) : null,
    },
    { field: "name", headerName: "หมวดหมู่", width: 300, flex: 1 },
    {
      field: "actions",
      headerName: "",
      flex: 0,
      headerAlign: "center",
      align: "center",
      renderCell: ({ id }) => {
        return [
          <GridActionsCellItem
            key={`${id}-edit`}
            icon={<EditNoteIcon />}
            label="Edit"
            color="primary"
            onClick={handleEditClick(id)}
          />,
        ];
      },
    },
    {
      field: "action",
      headerName: "หมวดหมู่ย่อย",
      width: 150,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const catId = params.row.categoryId;
        const category = categories.find((c) => c.id === catId);
        return (
          <Button variant="outlined" onClick={() => handleOpen(category)}>
            ดูรายการ
          </Button>
        );
      },
    },
  ];

  const fetchData = useCallback(async () => {
    try {
      const res = await services.getCategories();
      const categories = res.data.data;

      const categoriesWithIndex = categories.map((item, index) => ({
        ...item,
        no: index + 1,
        name: item.name,
        categoryId: item.id,
        // image: item.image ?? null,
      }));

      setCategories(categories);
      setRows(categoriesWithIndex);
      return categories;
    } catch (error) {
      console.error("Error fetching categories:", error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
      <AppBreadcrumbs />
      <Toaster position="bottom-left" reverseOrder={false} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
          my: 2,
        }}
      >
        {/* ส่วนซ้าย: หัวข้อ + Search */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography component="h2" variant="h5" sx={{ whiteSpace: "nowrap" }}>
            หมวดหมู่สินค้า
          </Typography>
          <TextField
            placeholder="ค้นหาด้วยหมวดหมู่"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
            sx={{ flex: 1, minWidth: { xs: "100%", sm: 300 }, maxWidth: 500 }}
          />
        </Box>

        {/* ปุ่มด้านขวา */}

        <Button
          color="success"
          variant="contained"
          onClick={() => {
            setCategoryInitialData(null);
            setCategoryDialogOpen(true);
          }}
        >
          เพิ่มรายการสินค้า
        </Button>
      </Box>

      <Box sx={{ flex: 1, position: "relative" }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <Paper sx={{ height: filtered.length === 0 ? 200 : "auto" }}>
            <DataGrid
              rows={filtered.map((p, idx) => ({
                id: p.id,
                no: idx + 1,
                ...p,
              }))}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 25 },
                },
              }}
              pageSizeOptions={[25, 50, 100]}
              localeText={{ noRowsLabel: "ไม่มีข้อมูล" }}
            />
          </Paper>
        </Box>
      </Box>

      {/* Modal แสดง SubCategory */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="h6">
              รายการหมวดหมู่ย่อย - {selectedCategory}
            </Typography>
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                setSubCategoryInitialData(null);
                setSubCategoryDialogOpen(true);
              }}
            >
              เพิ่มหมวดหมู่สินค้าย่อย
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Paper
            elevation={1}
            sx={{ height: subCategories.length === 0 ? 200 : "auto" }}
          >
            <DataGrid
              rows={subCategories.map((sub, index) => ({
                id: sub.id,
                name: sub.name,
                index: index + 1,
              }))}
              columns={[
                { field: "index", headerName: "ลำดับ", width: 90 },
                { field: "name", headerName: "ชื่อหมวดหมู่ย่อย", flex: 1 },
                {
                  field: "actions",
                  headerName: "",
                  flex: 0,
                  headerAlign: "center",
                  align: "center",
                  renderCell: ({ id }) => {
                    return [
                      <GridActionsCellItem
                        key={`${id}-edit-sub`}
                        icon={<EditNoteIcon />}
                        label="Edit"
                        color="primary"
                        onClick={handleEditSubCategoryClick(id)}
                      />,
                    ];
                  },
                },
              ]}
              hideFooterPagination
              disableRowSelectionOnClick
              localeText={{ noRowsLabel: "ไม่มีข้อมูล" }}
            />
          </Paper>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>ปิด</Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <CategoryFormDialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        onSuccess={() => {
          setLoading(true);
          fetchData();
        }}
        initialData={categoryInitialData}
      />

      {/* Sub-Category Dialog */}
      <SubCategoryFormDialog
        open={subCategoryDialogOpen}
        onClose={() => setSubCategoryDialogOpen(false)}
        categoryId={
          categories.find((c) => c.name === selectedCategory)?.id ?? null
        }
        onSuccess={async () => {
          setLoading(true);
          const updatedCategories = await fetchData();
          const updated = updatedCategories.find(
            (c) => c.name === selectedCategory
          );
          setSubCategories(updated?.subCategories || []);
        }}
        initialData={subCategoryInitialData}
      />
    </>
  );
};

export default Products;
